import { createEngineNode } from "./worklet-host.js";
import { EngineClient } from "../engine/engine-client.js";
import { Sonifier } from "../render/sonify.js";
import { store } from "../app/state/store.js";
import { DEFAULT_DSP_CONFIG, dspConfig, setDspConfig } from "../engine/dsp.js";
import { loadModel } from "../storage/model.js";
async function hasPersistedModel() {
    try {
        return (await loadModel()) !== null;
    }
    catch {
        return false;
    }
}
export async function buildGraph(stream, wasmModule, wasmFactory, engineConfig, glueUrl, wasmUrl, wasmBytes) {
    console.info(`[audio/graph] buildGraph wasm=${!!wasmModule} ` +
        `factory=${!!wasmFactory} ` +
        `glue=${!!glueUrl} ` +
        `tracks=${stream.getTracks().length}`);
    const ctx = new AudioContext({ latencyHint: "interactive" });
    console.debug(`[audio/graph] AudioContext sampleRate=${ctx.sampleRate} ` +
        `baseLatency=${ctx.baseLatency ?? "n/a"} state=${ctx.state}`);
    const source = ctx.createMediaStreamSource(stream);
    let node;
    try {
        node = await createEngineNode(ctx, wasmModule, glueUrl ?? null);
    }
    catch (err) {
        console.error("[audio/graph] failed to create engine node", err);
        void ctx.close();
        throw err;
    }
    const engine = new EngineClient(node);
    node.onprocessorerror = (e) => {
        console.error("[audio/graph] AudioWorkletProcessor error", e);
    };
    source.connect(node);
    const mute = ctx.createGain();
    mute.gain.value = 0;
    node.connect(mute).connect(ctx.destination);
    const sonifier = new Sonifier(ctx);
    sonifier.output.connect(ctx.destination);
    try {
        void probeMicGain(ctx, source);
    }
    catch (err) {
        console.warn("[audio/graph] mic gain probe failed to start", err);
    }
    await ctx.resume();
    console.debug(`[audio/graph] context resumed state=${ctx.state}`);
    try {
        void wasmFactory;
        await engine.init(wasmModule, null, engineConfig, glueUrl ?? null, wasmUrl ?? null, wasmBytes ?? null);
        console.info(`[audio/graph] engine ready usingWasm=${engine.usingWasm} ` +
            (engine.usingWasm
                ? "(native agility-rt active)"
                : "(JS DSP fallback active — see [EngineClient] warnings for " +
                    "the concrete reason WASM was not used)"));
    }
    catch (err) {
        console.error("[audio/graph] engine init failed", err);
        throw err;
    }
    return {
        ctx,
        engine,
        sonifier,
        source,
        resume: () => ctx.resume(),
        dispose: () => {
            console.info("[audio/graph] dispose");
            try {
                source.disconnect();
                node.disconnect();
                sonifier.dispose();
            }
            catch { }
            void ctx.close();
            for (const t of stream.getTracks())
                t.stop();
        },
    };
}
async function probeMicGain(ctx, source) {
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;
    source.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const start = ctx.currentTime;
    let peakRms = 0;
    let frames = 0;
    await new Promise((resolve) => {
        const sample = () => {
            analyser.getFloatTimeDomainData(buf);
            let sum = 0;
            for (let i = 0; i < buf.length; i++)
                sum += buf[i] * buf[i];
            const rms = Math.sqrt(sum / buf.length);
            if (rms > peakRms)
                peakRms = rms;
            frames++;
            if (ctx.currentTime - start < 0.8) {
                setTimeout(sample, 40);
            }
            else {
                resolve();
            }
        };
        sample();
    });
    try {
        source.disconnect(analyser);
    }
    catch { }
    const floor = Math.max(peakRms, 0.001);
    const gateTarget = dspConfig.energyGate * 0.25;
    let suggested = gateTarget / floor;
    suggested = Math.max(2, Math.min(40, suggested));
    suggested = Math.round(suggested * 2) / 2;
    console.info(`[audio/graph] mic gain probe: frames=${frames} ` +
        `noiseFloorRms=${floor.toFixed(4)} gateTarget=${gateTarget.toFixed(3)} ` +
        `suggestedEnergyScale=${suggested}`);
    store.suggestedEnergyScale.set(suggested);
    const SAFE_MIN_SCALE = 3;
    const appliedSuggestion = Math.max(SAFE_MIN_SCALE, suggested);
    const floorAtSuggested = floor * appliedSuggestion;
    const isUntuned = dspConfig.energyScale === DEFAULT_DSP_CONFIG.energyScale;
    const modelAlreadyLoaded = await hasPersistedModel();
    if (modelAlreadyLoaded) {
        console.warn("[audio/graph] a persisted model/corpus exists; NOT auto-applying " +
            `probe suggestion (${suggested}). Changing energyScale now would ` +
            "put inference in a different feature space than training. Retrain " +
            "under the current config if detection is poor.");
    }
    else if (isUntuned && floorAtSuggested < dspConfig.energyGate) {
        console.info("[audio/graph] energyScale is default; auto-applying probe " +
            `suggestion (${appliedSuggestion}); ambient floor maps to ` +
            `${floorAtSuggested.toFixed(3)} (< gate ${dspConfig.energyGate})`);
        setDspConfig({ energyScale: appliedSuggestion });
    }
    else if (isUntuned) {
        console.warn("[audio/graph] NOT auto-applying probe suggestion: it would map " +
            `the ambient floor to ${floorAtSuggested.toFixed(3)} (>= gate ` +
            `${dspConfig.energyGate}), which would saturate silence. Keeping ` +
            "the default energyScale; tune 'Loudness gain' manually if needed.");
    }
}
